import { computed, makeObservable, observable } from 'mobx';
import { StoreNode } from '../../store';
import { Store } from '../../store/store';
import { CloudNode, CloudNodeType } from './cloudNode';
import { CloudGraph } from './cloudGraph';
import { SimulationLinkDatum } from 'd3';

export interface Props {
  graph: CloudGraph,
  sourceId: string,
  targetId: string
}

export type CloudEdgeType =
  'TopicSpeaker' |
  'TopicTopic' |
  'SpeakerTopic' |
  'SpeakerSpeaker';

 
export class CloudEdge<_TSourceType extends CloudNodeType = 'Unknown', _TTargetType extends CloudNodeType = 'Unknown'>
  extends StoreNode
  implements SimulationLinkDatum<CloudNode> {

  constructor(store: Store, props: Props) {
    super(store, props);
    makeObservable(this, {
      sourceNode: computed,
      source: computed,
      targetNode: computed,
      target: computed,
      isVisible: computed,
      isAccented: computed,
      isSelected: computed,
      isExpanded: computed,
      disableTransitions: observable,
    });

    this.graph = props.graph;
    this.sourceId = props.sourceId;
    this.targetId = props.targetId;
  }

  get key() {
    return `(${this.sourceId},${this.targetId})`;
  }

  readonly sourceId: string;
  readonly targetId: string;
  readonly graph: CloudGraph;

  get sourceNode(): CloudNode | null {
    return this.graph.getNode(this.sourceId);
  }
  get source(): CloudNode {
    return this.sourceNode!;
  }

  get targetNode(): CloudNode | null {
    return this.graph.getNode(this.targetId);
  }
  get target(): CloudNode {
    return this.targetNode!;
  }

  get isVisible() {
    const src = this.sourceNode;
    const tgt = this.targetNode;
    if (!src || !tgt)
      return false;

    return !!(src.isVisible && tgt.isVisible);
  }

  get isAccented() {
    const src = this.sourceNode;
    const tgt = this.targetNode;
    if (!src || !tgt)
      return false;

    return !!(
      this.isVisible && (
        (src.isSelected && tgt.isAccented) ||
        (src.isAccented && tgt.isSelected)));
  }

  get isSelected() {
    return !!(this.sourceNode?.isSelected && this.targetNode?.isSelected);
  }

  get isExpanded() {
    return !!(this.sourceNode?.isExpanded && this.targetNode?.isExpanded);
  }

  strength = 1;
  distance = 1;

  disableTransitions = false;
}